'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import { PhnxAdviserinputsearch } from '@/components/01-atoms/Adviserinputsearch/Adviserinputsearch'
import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxOfficeinputsearch } from '@/components/01-atoms/Officeinputsearch/Officeinputsearch'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok-api'

import AdvisersView from './components/advisers'
import NearestView from './components/nearest'
import Offices from './components/offices'
import styles from './Searchfindcomponent.module.css'

type StoryblokHeaders = {
  total?: string | number
  [key: string]: unknown
}

// Optimized function to get office UUIDs for a specialty without loading full adviser data
const getOfficeUUIDsForSpecialty = async (
  specialty: string,
  location: string,
  filterLocation: string
): Promise<string[]> => {
  const uniqueOffices = new Set<string>()
  let currentPage = 1
  let hasMorePages = true
  let totalProcessed = 0
  const maxAdvisers = 500 // Reasonable limit to prevent excessive loading

  try {
    while (hasMorePages && totalProcessed < maxAdvisers) {
      const response = await getStoryblokApi().get('cdn/stories', {
        ...searchParamsData,
        filter_query: {
          component: { in: 'adviser' },
          specialities: { any_in_array: specialty },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        resolve_relations: 'offices',
        // Exclude all unnecessary fields to minimize payload
        excluding_fields:
          'content,title,givenName,surname,position,description,businessAddress,mobile,email,image,filename,profileUrl,specialities,settings,linkedinUrl,experience,created_at,published_at,first_published_at',
        per_page: 100,
        page: currentPage,
      })

      const { data } = response
      const headers: StoryblokHeaders = Object.fromEntries(Object.entries(response.headers))

      // Extract office UUIDs from resolved relations
      if (data?.rels != null && data.rels.length > 0) {
        data.rels.forEach((office: { uuid: string }) => {
          uniqueOffices.add(office.uuid)
        })
      }

      const total = Number(headers.total ?? '0')
      totalProcessed += data?.stories?.length ?? 0
      const currentCount = currentPage * 100
      hasMorePages = currentCount < total && totalProcessed < maxAdvisers
      currentPage++
    }

    return Array.from(uniqueOffices)
  } catch (error) {
    console.error('Error fetching office UUIDs for specialty:', error)
    return []
  }
}

export type SearchfindcomponentProps = { blok: { searchType: 'both' | 'office' | 'adviser' } }

export const PhnxSearchfindcomponent: React.FC<SearchfindcomponentProps> = ({ blok }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [specialties, setSpecialties] = useState<Array<{ value: string; name: string }>>([])
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('')
  const [specitlyOffices, setSpecitlyOffices] = useState<string[] | null>(null)

  const searchParams = useSearchParams()

  let location =
    process.env.NEXT_PUBLIC_NEXT_LOCATION != null && process.env.NEXT_PUBLIC_NEXT_LOCATION !== ''
      ? process.env.NEXT_PUBLIC_NEXT_LOCATION
      : 'au'
  let filterLocation = location === 'en-nz' ? 'au' : `en-nz`

  // Read URL parameters and set initial specialty (only when specialties are loaded)
  useEffect(() => {
    if (specialties.length === 0) return // Wait for specialties to load

    const specialtyParam = searchParams.get('specialty')
    if (specialtyParam != null && specialtyParam !== '') {
      // Normalize: decode URI, trim, and match against available specialties
      const normalizedParam = decodeURIComponent(specialtyParam)
        .replace(/\+/g, ' ')
        .trim()
        .toLowerCase()

      // Try to find exact match
      const found = specialties.find(
        (s) => s.value.toLowerCase() === normalizedParam || s.name.toLowerCase() === normalizedParam
      )
      if (found) {
        setSelectedSpecialty(found.value)
      } else {
        setSelectedSpecialty(normalizedParam) // fallback, may still work
      }
    }
  }, [searchParams, specialties])

  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const dimension =
          location === 'en-nz' ? 'nz' : (process.env.NEXT_PUBLIC_NEXT_LOCATION ?? 'au')

        const { data } = await getStoryblokApi().get('cdn/datasource_entries', {
          datasource: 'service-lines',
          per_page: 100, // Reduced from 1000
          sort_by: 'value:asc',
          dimension: dimension,
        })

        // Filter out entries with null values for the current location
        const filteredEntries = data.datasource_entries.filter(
          (entry: { value: string; name: string; dimension_value?: string }) => {
            return entry.dimension_value !== '' && entry.dimension_value !== 'null'
          }
        )

        setSpecialties(filteredEntries)
      } catch (error) {
        console.error('Error fetching specialties:', error)
        setSpecialties([]) // Fallback to empty array
      }
    }
    fetchSpecialties()
  }, [location])

  const handleSpecialtyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value === 'all') {
      setSelectedSpecialty('')
      setSpecitlyOffices(null)
    } else {
      setSelectedSpecialty(event.target.value)
    }
  }

  const handleReset = () => {
    setSelectedSpecialty('')
    setSpecitlyOffices(null)
  }

  // when specialty is changed to a specific value, fetch office UUIDs for that specialty
  useEffect(() => {
    if (selectedSpecialty && selectedSpecialty !== '') {
      const fetchOfficesForSpecialty = async () => {
        setIsLoading(true)
        try {
          const officeUUIDs = await getOfficeUUIDsForSpecialty(
            selectedSpecialty,
            location,
            filterLocation
          )
          setSpecitlyOffices(officeUUIDs.length > 0 ? officeUUIDs : [])
        } catch (error) {
          console.error('Error fetching offices for specialty:', error)
          setSpecitlyOffices(null)
        } finally {
          setIsLoading(false)
        }
      }
      fetchOfficesForSpecialty()
    } else {
      // When no specialty selected, don't pre-filter offices
      setSpecitlyOffices(null)
    }
  }, [selectedSpecialty, location, filterLocation])

  return (
    <>
      <div className={styles.searchfindcomponent}>
        {/* screen reader hidden h1 */}
        <h1 className="sr-only">Search for an office or adviser</h1>
        {/* Screen reader hidden loading */}
        {isLoading && <div className="sr-only">Loading...</div>}
        <div className={styles.searchForm}>
          <div className={styles.searchTitle}>
            {blok.searchType === 'both' || blok.searchType === 'office'
              ? 'Find an office by postcode or specialty'
              : 'Find an adviser by name or specialty'}
          </div>
          <div className={styles.searchBarForm}>
            <div className={styles.autocomplete}>
              {(blok.searchType === 'both' || blok.searchType === 'office') && (
                <PhnxOfficeinputsearch variant="primary" placeholder={'Search for an office'} />
              )}
              {blok.searchType === 'adviser' && (
                <PhnxAdviserinputsearch
                  variant="primary"
                  placeholder={'Search for an adviser'}
                  onSelect={(item) => {
                    if (item?.url != null) {
                      window.location.href = item.url
                    }
                  }}
                />
              )}
            </div>
            <div className={styles.formSep}>OR</div>
            <div>
              <label htmlFor="specialty" className="sr-only">
                Select a specialty
              </label>
              <select
                name="specialty"
                id="specialty"
                value={selectedSpecialty}
                onChange={handleSpecialtyChange}
                disabled={!specialties.length}
              >
                <option value="all">Select a specialty</option>
                {specialties.length > 0 &&
                  specialties
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((specialty: { value: string; name: string }, index) => (
                      <option key={index} value={specialty.value}>
                        {specialty.name}
                      </option>
                    ))}
              </select>
            </div>
          </div>
          <PhnxButton
            variant="tertiary"
            size="small"
            label="Reset"
            type="button"
            icon={'fx-icon-dismiss-lg'}
            iconPosition="left"
            onClick={handleReset}
          />
        </div>
        {blok.searchType === 'both' && (
          <NearestView selectedSpecialty={selectedSpecialty} specitlyOffices={specitlyOffices} />
        )}
        {blok.searchType === 'office' && (
          <Offices selectedSpecialty={selectedSpecialty} specitlyOffices={specitlyOffices} />
        )}
        {blok.searchType === 'adviser' && <AdvisersView selectedSpecialty={selectedSpecialty} />}
      </div>
    </>
  )
}
